import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiPlugin, StrapiApiScrollingLogo } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import ScrollingLogosWidget from '../../widgets/animated/ScrollingLogosWidget';
import AllIntegrationsShowcase from '../../widgets/showcases/AllIntegrationsShowcase';

type AIAssistantProductStaticProps = {
  plugins: StrapiApiPlugin[];
  scrollingLogos: StrapiApiScrollingLogo[];
};

export const getStaticProps: GetStaticProps<AIAssistantProductStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const plugins = await StrapiApiClient.getAllPlugins();
  const props: AIAssistantProductStaticProps = { plugins, scrollingLogos };
  return withGlobalDefaultStaticProps(context, { props });
};

const AIAssistantProduct: NextPageWithLayout<AIAssistantProductStaticProps> = (props) => {
  const { plugins, scrollingLogos } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.WorkAssistantForProduct);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Work Assistant for Product Teams"
        title="Smarter decisions, faster launches"
        description="Get instant insight from dev updates, customer feedback, and docs—so you can prioritize, plan, and ship with confidence."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrow={true}>
        <ScrollingLogosWidget logos={scrollingLogos} />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="AI capabilities for Product teams" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-sales-page-express-approvals'),
              orientation: 'left',
              title: 'Track Progress Without Interruptions',
              description:
                'Stay informed on every product change without disrupting your developers. Gain full visibility into updates and keep your team aligned at every stage.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-sales-page-personalised-info'),
              orientation: 'right',
              title: 'Fast, Accurate PRDs',
              description:
                'Generate and review complete product docs instantly, with all key requirements built in. Ensure clarity and speed for every product launch.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-sales-page-instant-answers'),
              orientation: 'left',
              title: 'Your Customers Are Talking. Start Listening',
              description:
                'Let GenAI uncover the insights hidden in every conversation—so you can build smarter and grow faster. Turn feedback into real product improvements.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-private-secure-support'),
              orientation: 'right',
              title: 'Confidential Product Collaboration',
              description:
                'Share sensitive product plans and collaborate securely across teams. Ensure privacy and compliance for every strategic initiative.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-self-service'),
              orientation: 'left',
              title: 'Self-Service Knowledge Hub',
              description:
                'Empower product teams to instantly access specs, feedback, and best practices. Reduce bottlenecks and enable faster, independent decision-making.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-employee-onboarding'),
              orientation: 'right',
              title: 'Frictionless Product Onboarding',
              description:
                'Accelerate onboarding for new product managers with automated delivery of key docs, processes, and team knowledge. Help new hires contribute from day one.',
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <AllIntegrationsShowcase plugins={plugins} hideSubtitle={true} />
      </PageSection>
    </>
  );
};
AIAssistantProduct.displayName = 'AIAssistantProduct';
export default AIAssistantProduct;
